<script lang="ts">
	import { browser } from '$app/environment';
	import { afterNavigate } from '$app/navigation';
	import { page } from '$app/state';
	import type { CityPageData } from '$lib/api/velatlas/response.types';
	import { ClientVelatlasLoggerApi } from '$lib/api/velatlas/velatlas.loggerApi.client';
	import { createLazyLoadAttachment } from '$lib/attachments/lazyLoad';
	import CityNav from '$lib/components/city/CityNav.svelte';
	import CityCard from '$lib/components/ui/CityCard.svelte';
	import DiscoveryCard from '$lib/components/ui/DiscoveryCard.svelte';
	import ExpandedFeatureCard from '$lib/components/ui/ExpandedFeatureCard.svelte';
	import FactsCard from '$lib/components/ui/factsCard/FactsCard.svelte';
	import FaqList from '$lib/components/ui/FaqList.svelte';
	import FeatureCard from '$lib/components/ui/FeatureCard.svelte';
	import HeroBgImage from '$lib/components/ui/HeroBgImage.svelte';
	import type { MapPoint } from '$lib/components/ui/map/_other/mapPoint.type';
	import NativeText from '$lib/components/ui/NativeText.svelte';
	import OfficialLinks from '$lib/components/ui/OfficialLinks.svelte';
	import QuickStat, { QuickStatSeparator } from '$lib/components/ui/QuickStat.svelte';
	import Story from '$lib/components/ui/Story.svelte';
	import Videos from '$lib/components/ui/Videos.svelte';
	import { Importance } from '$lib/features/_other/importance.enum';
	import type { PlaceLocation } from '$lib/features/_other/placeLocation';
	import { YEAR_SEASON_COLOURS, YEAR_SEASON_ICONS, YearSeason } from '$lib/features/_other/yearSeason.enum';
	import type { ThumbnailCity } from '$lib/features/city/cityThumbnail.type';
	import type { CityDetails } from '$lib/features/cityDetails/cityDetails.type';
	import type { CityFeature, CityFeaturePreview } from '$lib/features/cityFeature/cityFeature.type';
	import {
		CITY_FEATURE_TYPE_ICON_MAP,
		CITY_FEATURE_TYPE_LABELS,
		CityFeatureType
	} from '$lib/features/cityFeature/cityFeatureType.enum';
	import type { CountryPreview } from '$lib/features/country/countryPreview.type';
	import type { Language } from '$lib/features/country/language/language.type';
	import type { CoverImage } from '$lib/features/image/imageDetails.type';
	import { INSIDER_TIP_ICONS } from '$lib/features/insiderTip/insiderTipCategory.enum';
	import { ANALYTICS_EVENT_NAMES, FULL_SITE_URL, PERSISTED_STATE_VISITED_CITIES } from '$lib/statics';
	import { serializeJsonLd } from '$lib/utils/schema.util';
	import { formatDownNumber } from '$lib/utils/time.util';
	import { UrlUtil, type NavLink } from '$lib/utils/url.util';
	import { PersistedState } from 'runed';
	import { onMount, type Component, type Snippet } from 'svelte';
	import type { Attachment } from 'svelte/attachments';
	import { SvelteSet } from 'svelte/reactivity';
	import IconAccountGroup from '~icons/mdi/account-group';
	import IconArrowRight from '~icons/mdi/arrow-right';
	import IconBookOpen from '~icons/mdi/book-open';
	import IconCityVariant from '~icons/mdi/city-variant';
	import IconFaq from '~icons/mdi/faq';
	import IconHandshake from '~icons/mdi/handshake';
	import IconHourglassEmpty from '~icons/mdi/hourglass-empty';
	import IconLandscape from '~icons/mdi/landscape';
	import IconLightbulb from '~icons/mdi/lightbulb';
	import IconMap from '~icons/mdi/map';
	import IconMapMarker from '~icons/mdi/map-marker';
	import IconPartyPopper from '~icons/mdi/party-popper';
	import IconPerson from '~icons/mdi/person';
	import IconPulse from '~icons/mdi/pulse';
	import IconSchool from '~icons/mdi/school';
	import IconTag from '~icons/mdi/tag';
	import IconThermometer from '~icons/mdi/thermometer';
	import type { Output } from './+page.server';

	type FeatureLayout = 'grid' | 'carousel' | 'list' | 'compact' | 'image-card' | 'itinerary';
	interface CityFeatureItem {
		title: string;
		pluralNameWording: string;
		sidebarLabel: string;
		sidebarId: string;
		type: CityFeatureType;
		features: CityFeaturePreview[];
		layout: FeatureLayout;
		Icon: Component;
	}

	const visitedCities = new PersistedState<SvelteSet<string>>(PERSISTED_STATE_VISITED_CITIES, new SvelteSet<string>([]), {
		serializer: {
			serialize: (value) => JSON.stringify(Array.from(value)),
			deserialize: (value) => new SvelteSet(JSON.parse(value))
		}
	});
	const analyticsApi = new ClientVelatlasLoggerApi();

	const { data }: { data: { output: Output } } = $props();

	const sectionVisibility = $state<Record<string, boolean>>({});
	let loadMap = $state<boolean>(false);

	const location = $derived<PlaceLocation>(data.output.location);
	const city = $derived<CityPageData>(data.output.cityData);
	const details = $derived<CityDetails>(city.details);
	const mainFeature = $derived<CityFeature>(city.feature);
	const country = $derived<CountryPreview>(data.output.country);
	const language = $derived<Language>(data.output.countryLanguage);
	const mapPoints = $derived<MapPoint[]>(data.output.mapPoints);
	const totalFeatures = $derived<number>(data.output.totalFeatures);
	const similarCities = $derived<ThumbnailCity[]>(data.output.similarCities || []);
	const featureGrids = $derived<CityFeatureItem[]>(
		[
			createFeatureItem({
				title: `Top Attractions in ${mainFeature.englishName}`,
				pluralNameWording: 'Attractions',
				type: CityFeatureType.ATTRACTION,
				features: city.attractions,
				layout: 'grid'
			}),
			createFeatureItem({
				title: `Best Views`,
				pluralNameWording: 'Viewpoints',
				type: CityFeatureType.VIEWPOINT,
				features: city.viewpoints,
				layout: 'image-card'
			}),
			createFeatureItem({
				title: `${mainFeature.englishName} Itineraries`,
				pluralNameWording: 'Itineraries',
				type: CityFeatureType.ITINERARY,
				features: city.itineraries,
				layout: 'itinerary'
			}),
			createFeatureItem({
				title: `Food in ${mainFeature.englishName}`,
				pluralNameWording: 'Foods',
				type: CityFeatureType.FOOD,
				features: city.foods,
				layout: 'carousel'
			}),
			createFeatureItem({
				title: `UNESCO World Heritage Sites`,
				pluralNameWording: 'World Heritage Sites',
				type: CityFeatureType.UNESCO_SITE,
				features: city.unescoSites,
				layout: 'grid'
			}),
			createFeatureItem({
				title: `Free Things to do in ${mainFeature.englishName}`,
				pluralNameWording: 'Free Things To Do',
				type: CityFeatureType.FREE_THING_TO_DO,
				features: city.freeThingToDos,
				layout: 'compact'
			}),
			createFeatureItem({
				title: `Parks & Nature`,
				pluralNameWording: 'Parks & Nature',
				type: CityFeatureType.GREEN_SPACE,
				features: city.greenSpaces,
				layout: 'grid'
			}),
			createFeatureItem({
				title: 'Highlights & Must-Sees',
				pluralNameWording: 'Highlights',
				type: CityFeatureType.CITY_HIGHLIGHT,
				features: city.cityHighlights,
				layout: 'grid'
			}),
			createFeatureItem({
				title: `Best Walks in ${mainFeature.englishName}`,
				pluralNameWording: 'Walk Routes',
				type: CityFeatureType.SCENIC_STROLL,
				features: city.scenicStrolls,
				layout: 'list'
			}),
			createFeatureItem({
				title: `Hidden Gems in ${mainFeature.englishName}`,
				pluralNameWording: 'Hidden Gems',
				type: CityFeatureType.HIDDEN_GEM,
				features: city.hiddenGems,
				layout: 'grid'
			}),
			createFeatureItem({
				title: 'Architecture & Landmarks',
				pluralNameWording: 'Landmarks',
				type: CityFeatureType.ARCHITECTURAL_HIGHLIGHT,
				features: city.architecturalHighlights,
				layout: 'grid'
			}),
			createFeatureItem({
				title: 'Markets & Shopping',
				pluralNameWording: 'Markets',
				type: CityFeatureType.MARKET,
				features: city.markets,
				layout: 'carousel'
			}),
			createFeatureItem({
				title: `${mainFeature.englishName} Nightlife`,
				pluralNameWording: 'Nightlife Spots',
				type: CityFeatureType.NIGHTLIFE_SPOT,
				features: city.nightlifeSpots,
				layout: 'carousel'
			}),
			createFeatureItem({
				title: 'Photo Spots',
				pluralNameWording: 'Photo Spots',
				type: CityFeatureType.PHOTO_SPOT,
				features: city.photoSpots,
				layout: 'image-card'
			}),
			createFeatureItem({
				title: 'Activities & Experiences',
				pluralNameWording: 'Activities',
				type: CityFeatureType.ACTIVITY,
				features: city.activities,
				layout: 'compact'
			}),
			createFeatureItem({
				title: 'Cycling Routes',
				pluralNameWording: 'Cycling Routes',
				type: CityFeatureType.SCENIC_BIKE_RIDE,
				features: city.scenicBikeRides,
				layout: 'list'
			}),
			createFeatureItem({
				title: 'Self-Guided Tours',
				pluralNameWording: 'Self-Guided Tours',
				type: CityFeatureType.SELF_GUIDED_TOUR,
				features: city.selfGuidedTours,
				layout: 'list'
			}),
			createFeatureItem({
				title: 'Annual Festivals & Events',
				pluralNameWording: 'Festivals',
				type: CityFeatureType.ANNUAL_FESTIVAL,
				features: city.annualFestivals,
				layout: 'list'
			})
		].filter(Boolean) as CityFeatureItem[]
	);
	const sections = $derived<{ id: string; label: string; Icon: Component }[]>([
		{ id: 'history', label: 'History', Icon: IconBookOpen },
		{ id: 'map', label: 'Map', Icon: IconMap },
		...featureGrids.map((item) => ({ id: item.sidebarId, label: item.sidebarLabel, Icon: item.Icon })),
		{ id: 'tips', label: 'Insider Tips', Icon: IconHandshake },
		{ id: 'faqs', label: 'FAQ', Icon: IconFaq }
	]);
	let activeSectionId = $derived.by<string>(() => {
		for (const section of sections) {
			if (sectionVisibility[section.id]) {
				return section.id;
			}
		}
		return 'history'; // Fallback
	});
	const meta = $derived.by(() => {
		const cityName = mainFeature.englishName;
		const cityAge = new Date().getFullYear() - city.details.foundedYear;
		const listFormatter = new Intl.ListFormat('en-GB', { style: 'long', type: 'conjunction' });
		const tagsPhrase =
			city.details.tags.length > 0 ? listFormatter.format(city.details.tags.slice(0, 3).map((t) => t.toLowerCase())) : '';

		const hook = `With over ${formatDownNumber(cityAge)} years of history — ${cityName} blends ${tagsPhrase}.`;
		const cta = 'Explore markets, hidden gems, walking & cycling routes, curated itineraries & much more.';

		const title = `${cityName} City Guide${!city.isUniqueName ? `, ${country.englishName}` : ''}: ${totalFeatures} Things To Do, Map & Itineraries`;
		const description = `${hook} ${cta}`;

		return {
			title,
			description,
			canonicalUrl: `${FULL_SITE_URL}/${UrlUtil.toCity({ location }).href}`,
			imageUrl: UrlUtil.getFullImageUrl(mainFeature.images[0])
		};
	});
	const combinedJsonLd = $derived([data.output.jsonLdBreadcrumbs, data.output.jsonLdCity, data.output.jsonLdFAQPage]);

	const observeMapLoading = createLazyLoadAttachment(() => (loadMap = true));

	const observeSection: Attachment = (node: Element) => {
		const id = node.id;
		const observer = new IntersectionObserver(
			(entries) => entries.forEach((entry) => (sectionVisibility[id] = entry.isIntersecting)),
			{
				rootMargin: '-20% 0px -60% 0px', // Active zone is a strip near the top
				threshold: 0
			}
		);
		observer.observe(node);
		return () => {
			observer.disconnect();
			delete sectionVisibility[id];
		};
	};

	function handleSectionClick(id: string) {
		activeSectionId = id;
		const el = document.getElementById(id);
		if (el) {
			const y = el.getBoundingClientRect().top + window.scrollY - 140;
			const onFinish = () => {
				window.removeEventListener('scrollend', onFinish);
			};
			window.addEventListener('scrollend', onFinish);
			window.scrollTo({ top: y, behavior: 'smooth' });
		}
	}

	function getNavToFeature(feature: CityFeaturePreview): NavLink {
		return UrlUtil.toCityFeature({ location, type: feature.type, featureSlug: feature.slug });
	}

	function createFeatureItem({
		title,
		pluralNameWording,
		type,
		features,
		layout
	}: {
		title: string;
		pluralNameWording: string;
		type: CityFeatureType;
		features: CityFeaturePreview[];
		layout: FeatureLayout;
	}): CityFeatureItem | null {
		if (features.length === 0) return null;
		const sidebarLabel = CITY_FEATURE_TYPE_LABELS[type];
		const sidebarId = sidebarLabel.toLowerCase().replaceAll(' ', '-');
		return {
			title: title,
			pluralNameWording: pluralNameWording,
			sidebarLabel: sidebarLabel,
			sidebarId: sidebarId,
			type: type,
			features: features,
			layout: layout,
			Icon: CITY_FEATURE_TYPE_ICON_MAP[type]
		};
	}

	function getTempBarWidth(temp: number): string {
		const TEMP_MIN = -10; // 0% bar width
		const TEMP_MAX = 40; // 100% bar width
		const percentage = ((temp - TEMP_MIN) / (TEMP_MAX - TEMP_MIN)) * 100;
		return `${Math.min(100, Math.max(0, percentage))}%`;
	}

	onMount(() => {
		const cityId = city.id;
		visitedCities.current = new SvelteSet([...visitedCities.current, cityId]);
	});

	// Effect to cleanly scroll the table of contents
	$effect(() => {
		let firstVisibleId = null;
		for (const section of sections) {
			const topMostVisibleSection = sectionVisibility[section.id];
			if (topMostVisibleSection) {
				firstVisibleId = section.id;
				break;
			}
		}
		if (firstVisibleId && firstVisibleId !== activeSectionId) {
			activeSectionId = firstVisibleId;
		}
	});

	afterNavigate((navigation) => {
		const isFromRandomGoto: boolean = page.state.isRandomCityNavigation ?? false;
		if (navigation.from === null || !isFromRandomGoto) return;
		analyticsApi.sendAnalytics({
			event: ANALYTICS_EVENT_NAMES.random,
			payload: {
				type: 'city'
			},
			uri: navigation.from.route.id ?? undefined,
			url: navigation.from.url.pathname
		});
	});
</script>

<svelte:head>
	<title>{meta.title}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={meta.canonicalUrl} />

	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:url" content={meta.canonicalUrl} />
	<meta property="og:image" content={meta.imageUrl} />
	<meta property="og:type" content="website" />

	<meta property="twitter:title" content={meta.title} />
	<meta property="twitter:description" content={meta.description} />
	<meta property="twitter:url" content={meta.canonicalUrl} />
	<meta property="twitter:image" content={meta.imageUrl} />
	<meta property="twitter:card" content="summary_large_image" />

	{@html `<script type="application/ld+json">${serializeJsonLd(combinedJsonLd)}</script>`}
</svelte:head>

<div class="flex min-h-screen flex-col gap-7">
	{@render Hero()}

	<div class="container mx-auto px-4">
		{@render Section(`${mainFeature.englishName} DNA`, CityDNA)}
	</div>

	<div class="relative container mx-auto flex flex-col gap-7 px-4 md:flex-row">
		<CityNav {sections} {activeSectionId} onSectionClick={handleSectionClick} />

		<div class="flex min-w-0 flex-1 flex-col gap-14">
			<section id="history" class="scroll-mt-32 md:scroll-mt-20" {@attach observeSection}>
				{@render Section(`History of ${mainFeature.englishName}`, HistorySnippet)}
			</section>
			<section id="map" class="scroll-mt-32 md:scroll-mt-20" {@attach observeSection}>
				{@render Section(`Map of ${mainFeature.englishName}`, Map)}
			</section>

			{#if details.funFacts.length > 0 || details.educationalFacts.length > 0}
				<section class="grid grid-cols-1 gap-7 lg:grid-cols-2">
					{@render Facts()}
				</section>
			{/if}

			<OfficialLinks
				websiteUrl={mainFeature.place?.websiteUrl}
				facebookId={mainFeature.place?.facebookId}
				instagramId={mainFeature.place?.instagramId}
				unescoId={mainFeature.place?.unescoId}
			/>

			{#if mainFeature.videos.length > 0}
				{@render Section(`Videos of ${mainFeature.englishName}`, VideosSnippet)}
			{/if}
			{@render Section(`Seasons`, SeasonHighlights)}

			{#each featureGrids as group (group.type)}
				{#if group.features.length > 0}
					{@render FeatureRenderer(group)}
				{/if}
			{/each}

			{#if city.tips.length > 0}
				<section id="tips" class="scroll-mt-32 md:scroll-mt-20" {@attach observeSection}>
					{@render Section('Insider Tips', Tips)}
				</section>
			{/if}

			<div class="flex flex-col items-start gap-3 lg:flex-row">
				<div class="flex flex-col items-center justify-center gap-3">
					<div class="flex flex-row items-center justify-center gap-2">
						<IconAccountGroup class="text-lg text-primary" />
						<h3 class="text-sm font-bold tracking-widest opacity-70">PERFECT FOR</h3>
					</div>
					<p class="text-base-content/70">{details.cityFor}</p>
				</div>

				<div class="flex flex-col items-center justify-center gap-3">
					<div class="flex flex-row items-center justify-center gap-2">
						<IconPulse class="text-lg text-primary" />
						<h3 class="text-sm font-bold tracking-widest opacity-70">VIBE</h3>
					</div>
					<p class="text-base-content/70">{details.vibe}</p>
				</div>
			</div>

			<section id="faqs" class="scroll-mt-32 md:scroll-mt-20" {@attach observeSection}>
				{@render Section(`Frequently Asked Questions about ${mainFeature.englishName}`, FAQ)}
			</section>
		</div>
	</div>

	{#if similarCities.length > 0}
		<section class="container mx-auto px-4">
			<div class="divider"></div>
			{@render SimilarCities()}
		</section>
	{/if}
</div>

{#snippet Hero()}
	{const feature = $derived<CityFeature>(city.feature)}
	{const avgTemp = $derived<number>(
		Math.round(
			(details.avgTempSpringCelsius +
				details.avgTempSummerCelsius +
				details.avgTempAutumnCelsius +
				details.avgTempWinterCelsius) /
				4
		)
	)}
	{const populationPercentage = $derived<number>(parseFloat(((details.population / country.population) * 100).toFixed(2)))}

	<HeroBgImage
		breadcrumbs={data.output.breadcrumbs}
		header={{
			title: feature.englishName,
			subtitle: `Full City Guide, Map & ${data.output.totalFeatures} Things To Do`,
			description: feature.description
		}}
		imageGallery={{ images: feature.images, internalClasses: 'lg:h-[80vh]', ariaLabel: `View ${feature.englishName} gallery` }}
		decorations={{ coordinates: data.output.mapPoints.slice(0, 15).map((item) => item.coords) }}
	>
		{#snippet QuickStatsSnippet()}
			{const age = $derived<number>(new Date().getFullYear() - details.foundedYear)}
			{#if feature.nativeName}
				{@render NativeName()}
				{@render QuickStatSeparator()}
			{/if}

			{#if details.isCapital}
				{@render Capital()}
				{@render QuickStatSeparator()}
			{/if}

			{@render FoundingYear()}
			{@render QuickStatSeparator()}

			{@render Age()}
			{@render QuickStatSeparator()}

			{@render SeasonsTemps()}
			{@render QuickStatSeparator()}

			{#if feature.place?.elevationMeters}
				{@render Elevation()}
				{@render QuickStatSeparator()}
			{/if}

			{@render Population()}

			{#snippet NativeName()}
				{#if feature.nativeName}
					<QuickStat
						value={feature.nativeName}
						valueNativeLanguage={{ languageCode: language.code, isLanguageRTL: language.rtl }}
					/>
				{/if}
			{/snippet}

			{#snippet Capital()}
				<QuickStat title="Capital city of {country.englishName}" value="CAPITAL" />
			{/snippet}

			{#snippet FoundingYear()}
				<QuickStat
					title="City founded in {details.foundedYear}, making it {age} years old"
					label="FOUNDED"
					value={details.foundedYear}
				/>
			{/snippet}

			{#snippet Age()}
				<QuickStat label="YEARS OLD" value={age} />
			{/snippet}

			{#snippet SeasonsTemps()}
				<div class="group relative">
					<div class="peer flex cursor-pointer border-b border-dashed border-white/30" role="button" tabindex="0">
						<QuickStat value={`${avgTemp}°C`} />
					</div>

					<div
						class="invisible fixed top-1/2 right-4 left-4 z-100 mx-auto max-w-xs -translate-y-1/2 rounded-box opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 peer-focus:visible peer-focus:opacity-100 md:absolute md:top-full md:left-1/2 md:mt-4 md:w-72 md:-translate-x-1/2 md:translate-y-0"
						tabindex="-1"
					>
						<div class="rounded-2xl border border-base-content/10 bg-base-100 p-5 text-base-content/80 shadow-2xl">
							<div class="mb-4 flex items-center justify-between border-b border-base-content/10 pb-2">
								<span class="text-xs font-bold tracking-widest">SEASONAL AVG</span>
								<IconThermometer />
							</div>

							<div class="space-y-4">
								{@render TempRow('Spring', details.avgTempSpringCelsius, 'bg-success')}
								{@render TempRow('Summer', details.avgTempSummerCelsius, 'bg-error')}
								{@render TempRow('Autumn', details.avgTempAutumnCelsius, 'bg-warning')}
								{@render TempRow('Winter', details.avgTempWinterCelsius, 'bg-info')}
							</div>
						</div>
					</div>
				</div>

				{#snippet TempRow(label: string, temp: number, bgColor: string)}
					<div class="flex items-center justify-between text-sm">
						<span class="w-16 font-medium">{label}</span>
						<div class="mx-3 h-2 flex-1 overflow-hidden rounded-full bg-base-200/50">
							<div class="h-full rounded-full {bgColor}" style="width: {getTempBarWidth(temp)}"></div>
						</div>
						<span class="w-10 text-right font-mono font-bold">{temp}°</span>
					</div>
				{/snippet}
			{/snippet}

			{#snippet Elevation()}
				<QuickStat
					title="Elevation above sea level: {feature.place?.elevationMeters} meters"
					label="ELEVATION"
					value={`${feature.place?.elevationMeters}m`}
				/>
			{/snippet}

			{#snippet Population()}
				<div class="group relative">
					<div class="peer flex cursor-pointer border-b border-dashed border-white/30" role="button" tabindex="0">
						<QuickStat label="POP." value={`${populationPercentage}%`} valueFirst={false} />
					</div>

					<div
						class="invisible fixed top-1/2 right-4 left-4 z-100 mx-auto max-w-xs -translate-y-1/2 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 peer-focus:visible peer-focus:opacity-100 md:absolute md:top-full md:left-1/2 md:mt-2 md:w-72 md:-translate-x-1/2 md:translate-y-0"
						tabindex="-1"
					>
						<div class="rounded-2xl border border-base-content/10 bg-base-100 p-5 text-base-content/80 shadow-2xl">
							<div class="mb-2 flex items-center justify-between border-b border-base-content/10 pb-2 text-base-content/70">
								<span class="text-xs font-bold tracking-widest">DEMOGRAPHICS</span>
								<IconPerson />
							</div>
							<p class="text-sm">
								<span class="font-mono font-bold text-primary/90">{details.population.toLocaleString()}</span> people live here.
								<br />
								That's <strong class="text-primary/90">{populationPercentage}%</strong> of the entire population of {country.englishName}.
							</p>
						</div>
					</div>
				</div>
			{/snippet}
		{/snippet}
	</HeroBgImage>
{/snippet}

{#snippet CityDNA()}
	<div class="scrollbar-hide flex snap-x snap-mandatory gap-6 overflow-x-auto pb-6">
		{@render Tag({
			cardBg: 'bg-fuchsia-500/5',
			borderColour: 'border-fuchsia-500/20',
			iconColour: 'text-fuchsia-500',
			badgeBg: 'bg-fuchsia-500/20',
			Icon: IconTag,
			title: 'KNOWN FOR',
			items: city.details.tags,
			createNavLink: (item: string) => UrlUtil.toCities({ filters: { tags: [item] } })
		})}

		{@render Tag({
			cardBg: 'bg-emerald-500/5',
			borderColour: 'border-emerald-500/20',
			iconColour: 'text-emerald-500',
			badgeBg: 'bg-emerald-500/20',
			Icon: IconLandscape,
			title: 'LANDSCAPE',
			items: city.details.landscapes,
			createNavLink: (item: string) => UrlUtil.toCities({ filters: { landscapes: [item] } })
		})}

		{@render Tag({
			cardBg: 'bg-indigo-500/5',
			borderColour: 'border-indigo-500/20',
			iconColour: 'text-indigo-500',
			badgeBg: 'bg-indigo-500/20',
			Icon: IconCityVariant,
			title: 'CITYSCAPE',
			items: city.details.cityscapes,
			createNavLink: (item: string) => UrlUtil.toCities({ filters: { cityscapes: [item] } })
		})}

		{@render Tag({
			cardBg: 'bg-sky-500/5',
			borderColour: 'border-sky-500/20',
			iconColour: 'text-sky-500',
			badgeBg: 'bg-sky-500/20',
			Icon: IconThermometer,
			title: 'CLIMATE',
			items: city.details.climates,
			createNavLink: (item: string) => UrlUtil.toCities({ filters: { climates: [item] } })
		})}

		<div class="w-2 shrink-0 md:hidden"></div>
	</div>

	{#snippet Tag({
		cardBg,
		borderColour,
		iconColour,
		badgeBg,
		Icon,
		title,
		items,
		createNavLink: createNav
	}: {
		cardBg: string;
		borderColour: string;
		iconColour: string;
		badgeBg: string;
		Icon: Component;
		title: string;
		items: string[];
		createNavLink: (tag: string) => NavLink;
	})}
		<div
			class="card {cardBg} flex w-[90vw] shrink-0 snap-center border {borderColour} group relative overflow-hidden rounded-3xl shadow-sm md:w-auto md:flex-1"
		>
			<div
				class="pointer-events-none absolute right-[-10%] bottom-[-10%] z-0 opacity-10 transition-opacity select-none group-hover:opacity-20 {iconColour}"
			>
				<Icon style="font-size: 8rem; width: 8rem; height: 8rem" />
			</div>

			<div class="card-body relative z-10 text-base-content">
				<h3 class="card-title">
					{title}
				</h3>
				<div class="flex flex-wrap gap-2">
					{#each items as item (item)}
						{const nav = $derived<NavLink>(createNav(item))}
						<a
							class="badge link {badgeBg} border-0 hover:bg-base-content/10"
							href="/{nav.href}"
							data-sveltekit-preload-data={nav.preloadData}
							data-sveltekit-preload-code={nav.preloadCode}
						>
							{item}
						</a>
					{/each}
				</div>
			</div>
		</div>
	{/snippet}
{/snippet}

{#snippet HistorySnippet()}
	<Story story={details.story} events={city.timelineEvents} />
{/snippet}

{#snippet VideosSnippet()}
	<Videos videos={mainFeature.videos} englishName={mainFeature.englishName} />
{/snippet}

{#snippet Map()}
	<figure aria-label="Map of {mainFeature.englishName}">
		<div class="mb-4 alert flex items-start gap-3 alert-soft text-left alert-info">
			<IconLightbulb class="mt-0.5 shrink-0 self-center text-xl" />
			<div class="flex flex-col gap-1">
				<h3 class="text-sm leading-tight font-bold">Interactive Destination & Itinerary Map</h3>
				<p class="text-xs leading-relaxed">
					This map blends our curated city guides—featuring
					<strong class="font-semibold"> best photo spots, scenic walks, viewpoints, hidden gems and much more </strong>—with a
					live Wikipedia explorer. Click on our numbered pins to plan your routes, or zoom in on the surrounding neighborhoods to
					discover verified local landmarks, museums, and stories with dynamic Wikipedia summaries and photos!
				</p>
			</div>
		</div>

		<div
			{@attach observeMapLoading}
			class="relative z-0 h-[50vh] min-h-117 w-full overflow-hidden rounded-box shadow-sm md:h-[70vh] md:min-h-125"
		>
			{#if browser && loadMap}
				{#await import('$lib/components/city/CityMap.svelte') then { default: CityMap }}
					<CityMap points={mapPoints} />
				{/await}
			{:else}
				<div class="flex h-full w-full items-center justify-center bg-base-200 text-base-content/70">
					<span class="loading mr-2 loading-spinner text-primary"></span>
					<span>Loading map of {mapPoints.length} points...</span>
				</div>
			{/if}
		</div>

		<figcaption class="sr-only">
			Interactive map showing locations of {mapPoints.length} points in {mainFeature.englishName}, attractions, parks and hidden
			gems among them.
		</figcaption>
	</figure>
{/snippet}

{#snippet Facts()}
	{#if details.funFacts.length > 0}
		<FactsCard sectionTitle="Fun Facts" Icon={IconPartyPopper} facts={details.funFacts.map((fact) => ({ content: fact }))} />
	{/if}

	{#if details.educationalFacts.length > 0}
		<FactsCard
			sectionTitle="Did You Know?"
			Icon={IconSchool}
			facts={details.educationalFacts.map((fact) => ({ content: fact }))}
		/>
	{/if}
{/snippet}

{#snippet Tips()}
	<div class="grid grid-cols-1 gap-3 md:grid-cols-2">
		{#each city.tips as tip (tip.tip)}
			{const Icon = $derived<Component>(INSIDER_TIP_ICONS[tip.category])}
			<div class="group card bg-secondary text-secondary-content">
				<div class="card-body">
					<div class="card-title text-secondary-content/70 transition-colors duration-150 group-hover:text-secondary-content">
						<Icon />
						{tip.category}
					</div>
					<p>{tip.tip}</p>
				</div>
			</div>
		{/each}
	</div>
{/snippet}

{#snippet FAQ()}
	<FaqList faqs={details.faqs} />
{/snippet}

{#snippet SimilarCities()}
	<div class="mb-10 flex flex-col items-center justify-center text-center">
		<h2 class="text-3xl md:text-5xl">
			Similar {similarCities.length === 1 ? 'Destination' : 'Destinations'} in
			<span class="text-primary">{country.englishName}</span>
		</h2>
		<p class="mt-4 max-w-2xl text-lg leading-relaxed font-light text-base-content/70">
			Continue your journey. Explore {similarCities.length === 1
				? 'this highly recommended city'
				: 'these highly recommended cities'} and uncover more of what {country.englishName} has to offer.
		</p>
	</div>

	<div class="grid grid-cols-1 justify-items-center gap-6 sm:grid-cols-2 lg:grid-cols-3">
		{#each similarCities as relatedCity (relatedCity.id)}
			<div class="w-full max-w-md">
				<CityCard city={relatedCity} eagerLoadCoverImage={false} />
			</div>
		{/each}
	</div>
{/snippet}

{#snippet Section(title: string, Snippet: Snippet)}
	<div class="flex flex-col gap-5">
		<h2 class="text-3xl">{title}</h2>
		{@render Snippet()}
	</div>
{/snippet}

{#snippet FeatureRenderer(group: CityFeatureItem)}
	{const Icon = $derived<Component>(CITY_FEATURE_TYPE_ICON_MAP[group.type])}
	<section id={group.sidebarId} class="scroll-mt-32 md:scroll-mt-20" {@attach observeSection}>
		<div class="mb-6 flex items-center justify-center border-b border-base-content/10 pb-4 md:items-end md:justify-between">
			{@render Header(Icon)}
		</div>
		{#if group.layout === 'grid'}
			{@render LayoutGrid(group.features)}
		{:else if group.layout === 'carousel'}
			{@render LayoutCarousel(group.features)}
		{:else if group.layout === 'list'}
			{@render LayoutList(group.features)}
		{:else if group.layout === 'image-card'}
			{@render LayoutImageCard(group.features)}
		{:else if group.layout === 'itinerary'}
			{@render LayoutItinerary(group.features)}
		{:else}
			{@render LayoutCompact(group.features)}
		{/if}
		<div class="mt-4 flex justify-end md:hidden">
			{@render ViewAllLink()}
		</div>
	</section>

	{#snippet Header(Icon: Component)}
		<h2 class="flex flex-col items-center justify-center gap-2 text-xl md:flex-row md:text-3xl">
			<Icon class="text-xl text-primary md:text-3xl" />
			<span>{group.title}</span>
		</h2>
		<div class="hidden md:block">
			{@render ViewAllLink()}
		</div>
	{/snippet}

	{#snippet ViewAllLink()}
		{const cityFeaturesNav = $derived<NavLink>(UrlUtil.toCityFeatures({ location, type: group.type }))}
		<a
			class="group btn btn-block rounded-full bg-primary/15 font-mono font-semibold text-primary shadow-xs transition-all duration-200 btn-sm hover:bg-primary hover:text-primary-content hover:shadow-md md:w-auto md:btn-md"
			href={`/${cityFeaturesNav.href}`}
			data-sveltekit-preload-data={cityFeaturesNav.preloadData}
			data-sveltekit-preload-code={cityFeaturesNav.preloadCode}
		>
			<span>view all {group.pluralNameWording.toLowerCase()}</span>
			<IconArrowRight class="transition-transform duration-200 group-hover:translate-x-1" />
		</a>
	{/snippet}
{/snippet}

{#snippet LayoutGrid(items: CityFeaturePreview[])}
	<ul class="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
		{#each items as item (item.id)}
			<li>
				<DiscoveryCard
					headingTag="h3"
					elementId={item.id}
					englishName={item.englishName}
					nativeName={null}
					description={item.description}
					{language}
					images={item.images}
					nav={getNavToFeature(item)}
					NoImagePlaceholderIcon={CITY_FEATURE_TYPE_ICON_MAP[item.type]}
				/>
			</li>
		{/each}
	</ul>
{/snippet}

{#snippet LayoutCarousel(items: CityFeaturePreview[])}
	<ul class="scrollbar-hide -mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-6 md:mx-0 md:px-0">
		{#each items as item (item.id)}
			{const featureNav = $derived<NavLink>(getNavToFeature(item))}
			<li class="group relative flex w-72 shrink-0 snap-center flex-col gap-1">
				<a
					href={`/${featureNav.href}`}
					data-sveltekit-preload-data={featureNav.preloadData}
					data-sveltekit-preload-code={featureNav.preloadCode}
					class="flex flex-col gap-1"
				>
					<div class="relative mb-3 h-40 overflow-hidden rounded-2xl bg-base-200 shadow-md">
						{@render FeatureImage(item)}
					</div>
					{@render Header(item)}
				</a>
			</li>
		{/each}
	</ul>

	{#snippet Header(item: CityFeaturePreview)}
		<h3 class="truncate text-base leading-tight transition-colors group-hover:text-primary">
			{item.englishName}
		</h3>
		{#if item.nativeName}
			<NativeText
				classes="truncate text-xs text-base-content/70"
				languageCode={data.output.countryLanguage.code}
				isLanguageRTL={data.output.countryLanguage.rtl}
				text={item.nativeName}
			/>
		{/if}
	{/snippet}
{/snippet}

{#snippet LayoutList(items: CityFeaturePreview[])}
	<ul class="space-y-4">
		{#each items as item (item.id)}
			{const featureNav = $derived<NavLink>(getNavToFeature(item))}
			<li>
				<a
					class="group flex flex-col gap-4 rounded-2xl border border-base-content/5 bg-base-100 p-4 shadow-xs transition-all hover:border-primary/20 hover:shadow-md md:flex-row md:gap-6"
					title="View more about {item.englishName}"
					href={`/${featureNav.href}`}
					data-sveltekit-preload-data={featureNav.preloadData}
					data-sveltekit-preload-code={featureNav.preloadCode}
				>
					<div class="flex flex-col items-center justify-center gap-4 lg:flex-row">
						<div class="relative h-32 w-full shrink-0 overflow-hidden rounded-xl bg-base-200 md:w-32">
							<div class="flex h-full w-full flex-col items-center justify-center text-center">
								{@render FeatureImage(item, true)}
							</div>
						</div>
						<div class="flex flex-1 flex-col gap-3 py-1">
							<div class="flex items-start justify-between">
								<h3 class="line-clamp-1 text-lg transition-colors group-hover:text-primary">{item.englishName}</h3>
							</div>
							{#if item.importance === Importance.ESSENTIAL}
								<span class="text-xs tracking-widest text-base-content/70 italic">HIGHLY RECOMMENDED</span>
							{/if}
							<p class="line-clamp-2 text-sm text-base-content/70 md:line-clamp-3">{item.description}</p>
						</div>
					</div>
				</a>
			</li>
		{/each}
	</ul>
{/snippet}

{#snippet LayoutItinerary(items: CityFeaturePreview[])}
	<div class="card border border-base-content/10 bg-base-200 shadow-md">
		{@render TripDuration()}
	</div>

	<ul class="mt-5 grid list-none grid-cols-1 gap-5 p-0 md:grid-cols-3">
		{#each items as item (item.id)}
			{const images = $derived(item.images)}
			<li>
				<ExpandedFeatureCard feature={item} {images} {location} />
			</li>
		{/each}
	</ul>

	{#snippet TripDuration()}
		<div class="card-body flex flex-col gap-4 p-6">
			<h3 class="flex flex-row gap-2 border-b border-base-content/10 pb-2">
				<IconHourglassEmpty /> TRIP PLANNER
			</h3>
			<p>
				<span class="text-xs font-bold">{details.stayDurationDays} Days Recommended</span>
			</p>
			<p class="text-sm leading-relaxed text-base-content/70">
				{details.stayDurationReasoning}
			</p>
		</div>
	{/snippet}
{/snippet}

{#snippet LayoutCompact(items: CityFeaturePreview[])}
	<ul class="grid grid-cols-1 gap-3 md:grid-cols-3">
		{#each items as item (item.id)}
			{const featureNav = $derived<NavLink>(getNavToFeature(item))}
			<li>
				<a
					class="group flex items-center gap-3 rounded-box border border-transparent bg-base-200/50 p-3 shadow-xs transition-colors hover:border-base-content/5 hover:shadow-sm"
					title="View more about {item.englishName}"
					href={`/${featureNav.href}`}
					data-sveltekit-preload-data={featureNav.preloadData}
					data-sveltekit-preload-code={featureNav.preloadCode}
				>
					<div class="relative h-10 w-10 shrink-0 overflow-hidden rounded-box bg-base-300">
						{@render ImageOrDot(item)}
					</div>
					<div class="min-w-0 overflow-hidden">
						{@render Header(item)}
					</div>
				</a>
			</li>
		{/each}
	</ul>

	{#snippet Header(item: CityFeaturePreview)}
		<h3 class="truncate text-sm transition-colors group-hover:text-primary" title={item.englishName}>{item.englishName}</h3>
		{#if item.nativeName}
			<NativeText
				classes="truncate text-xs opacity-70 max-w-full inline-block"
				languageCode={data.output.countryLanguage.code}
				isLanguageRTL={data.output.countryLanguage.rtl}
				text={item.nativeName}
			/>
		{:else}
			<span class="truncate text-xs opacity-70">View Details</span>
		{/if}
	{/snippet}

	{#snippet ImageOrDot(item: CityFeaturePreview)}
		{const firstImage = $derived<CoverImage | undefined>(item.images?.[0])}
		{#if firstImage}
			<img
				class="h-full w-full object-cover"
				src={UrlUtil.getFullImageUrl(firstImage)}
				alt={firstImage.altText}
				width={firstImage.width}
				height={firstImage.height}
				loading="lazy"
			/>
		{:else}
			<div class="flex h-full w-full items-center justify-center text-base-content/30">
				<span class="h-2 w-2 rounded-full bg-current"></span>
			</div>
		{/if}
	{/snippet}
{/snippet}

{#snippet LayoutImageCard(items: CityFeaturePreview[])}
	<ul class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
		{#each items as item (item.id)}
			<li>
				<FeatureCard feature={item} nav={getNavToFeature(item)} />
			</li>
		{/each}
	</ul>
{/snippet}

{#snippet SeasonHighlights()}
	{const items = $derived<CityFeaturePreview[]>(city.seasonalHighlights)}
	{const getStyles = (name: string) => {
		const n = name.toLowerCase();
		if (n.includes('spring')) return YEAR_SEASON_COLOURS[YearSeason.SPRING];
		if (n.includes('summer')) return YEAR_SEASON_COLOURS[YearSeason.SUMMER];
		if (n.includes('autumn')) return YEAR_SEASON_COLOURS[YearSeason.AUTUMN];
		if (n.includes('winter')) return YEAR_SEASON_COLOURS[YearSeason.WINTER];
		return YEAR_SEASON_COLOURS[YearSeason.SPRING];
	}}

	<div class="flex h-auto w-full flex-col gap-2 overflow-hidden rounded-3xl md:h-115 md:flex-row md:gap-0">
		{#each items as item (item.id)}
			{const styles = $derived(getStyles(item.englishName))}
			{const firstImage = $derived<CoverImage | undefined>(item.images?.[0])}
			{const lowerCaseName = $derived<string>(item.englishName.toLowerCase())}
			{const Icon = $derived<Component>(
				lowerCaseName.includes('spring')
					? YEAR_SEASON_ICONS[YearSeason.SPRING]
					: lowerCaseName.includes('summer')
						? YEAR_SEASON_ICONS[YearSeason.SUMMER]
						: lowerCaseName.includes('autumn')
							? YEAR_SEASON_ICONS[YearSeason.AUTUMN]
							: YEAR_SEASON_ICONS[YearSeason.WINTER]
			)}
			{const featureNav = $derived<NavLink>(getNavToFeature(item))}
			<a
				class="group relative min-h-37.5 flex-1 overflow-hidden transition-[flex-grow] duration-500 ease-in-out md:min-h-0 md:hover:grow-3"
				title="View more about {item.englishName} in {mainFeature.englishName}"
				href={`/${featureNav.href}`}
				data-sveltekit-preload-data={featureNav.preloadData}
				data-sveltekit-preload-code={featureNav.preloadCode}
			>
				{#if firstImage}
					{@render BgImage(firstImage)}
				{/if}
				{@render Overlay()}
				<div class="absolute inset-0 flex flex-col justify-end p-6">
					{@render Content(item, styles.text, Icon)}
				</div>
				{@render ColouredLine(styles.bg)}
			</a>
		{/each}
	</div>

	{#snippet BgImage(firstImage: CoverImage)}
		<img
			class="absolute inset-0 h-full w-full object-cover grayscale-30 transition-transform duration-1000 group-hover:scale-110 group-hover:grayscale-0"
			src={UrlUtil.getFullImageUrl(firstImage)}
			alt={firstImage.altText}
			width={firstImage.width}
			height={firstImage.height}
			loading="lazy"
		/>
	{/snippet}

	{#snippet Overlay()}
		<div
			class="absolute inset-0 bg-linear-to-t from-base-100 via-base-100/60 to-transparent transition-colors duration-500 md:via-base-100/30 md:group-hover:via-base-100/60"
		></div>
	{/snippet}

	{#snippet Content(item: CityFeaturePreview, textColour: string, Icon: Component)}
		<div class="translate-y-0 transform transition-all duration-500 md:translate-y-8 md:group-hover:translate-y-0">
			<h3 class="mb-2 text-lg {textColour} inline-flex gap-3 md:text-2xl lg:text-4xl">
				{item.englishName}
				<Icon class="duration-500 md:opacity-0 md:group-hover:opacity-100" />
			</h3>
			<p
				class="line-clamp-2 max-w-sm text-sm transition-opacity delay-100 duration-500 md:opacity-0 md:group-hover:opacity-100 lg:line-clamp-3"
			>
				{item.description}
			</p>
		</div>
	{/snippet}

	{#snippet ColouredLine(colour: string)}
		<div
			class="absolute right-0 bottom-0 left-0 h-1 opacity-50 transition-opacity duration-300 md:opacity-0 md:group-hover:opacity-100 {colour}"
		></div>
	{/snippet}
{/snippet}

{#snippet FeatureImage(item: CityFeaturePreview, iconMode = false)}
	{const firstImage = $derived<CoverImage | undefined>(item.images?.[0])}
	{#if firstImage}
		<img
			class="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
			src={UrlUtil.getFullImageUrl(firstImage)}
			alt={firstImage.altText}
			width={firstImage.width}
			height={firstImage.height}
			loading="lazy"
		/>
	{:else}
		<div class="flex h-full w-full flex-col items-center justify-center bg-base-200 p-4 text-center text-base-content/20">
			{#if iconMode}
				{@render OnlyIcon()}
			{:else}
				{@render IconWithName()}
			{/if}
		</div>
	{/if}

	{#snippet OnlyIcon()}
		<IconMapMarker class="text-3xl" />
	{/snippet}

	{#snippet IconWithName()}
		<IconMapMarker class="mb-2 text-5xl" />
		<span class="text-xs font-bold tracking-widest opacity-50">{item.englishName.toUpperCase()}</span>
	{/snippet}
{/snippet}
